/** SUNTRONIX ONE — Catalogue Enquiries inbox + link analytics (Sales Desk).
 *  Enquiries submitted through public catalogue links land here; the team
 *  actions them, updates status, and turns them into quotations. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Inbox, BarChart3, Mail, Phone, Building2, FileText, RefreshCw,
  Eye, MessageSquare, Layers,
} from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");

const STATUS = ["NEW", "CONTACTED", "QUOTED", "CLOSED"];
const STATUS_TONE = {
  NEW: "bg-indigo-50 text-indigo-700 border-indigo-200",
  CONTACTED: "bg-amber-50 text-amber-800 border-amber-200",
  QUOTED: "bg-blue-50 text-blue-700 border-blue-200",
  CLOSED: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function CatalogueEnquiries() {
  const [tab, setTab] = useState("enquiries");
  return (
    <div className="space-y-6 max-w-[1500px]" data-testid="catalogue-enquiries-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
          Sales Desk · Product Catalogue
        </div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
          <Inbox className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
          Catalogue Enquiries
        </h1>
        <p className="text-[12px] text-slate-500 mt-2">
          Leads from your shared catalogue links, and how each shared catalogue is performing.
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[["enquiries", "Enquiries", Inbox], ["analytics", "Link performance", BarChart3]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`enq-tab-${k}`}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors ${
              tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "enquiries" ? <EnquiriesTab /> : <AnalyticsTab />}
    </div>
  );
}


function EnquiriesTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/catalogue-collections/enquiries/all"); setRows(data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (eid, status) => {
    setRows((rs) => rs.map((r) => (r.id === eid ? { ...r, status } : r)));
    try { await api.patch(`/catalogue-collections/enquiries/${eid}`, { status }); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); load(); }
  };

  const createQuote = async (enq) => {
    try {
      const { data } = await api.get(`/catalogue-collections/${enq.collection_id}/quote-lines`);
      if (!data.items?.length) { toast.error("This collection has no priced products to quote"); return; }
      sessionStorage.setItem("st:quote-prefill", JSON.stringify({
        items: data.items,
        subject: enq.collection_name || data.subject_hint || "",
      }));
      if ((enq.status || "NEW") === "NEW") {
        setRows((rs) => rs.map((r) => (r.id === enq.id ? { ...r, status: "QUOTED" } : r)));
        try { await api.patch(`/catalogue-collections/enquiries/${enq.id}`, { status: "QUOTED" }); }
        catch { /* non-blocking */ }
      }
      navigate("/commercial/crm/quotations?new=1&prefill=1");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build quote"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-500">
          {rows.length} enquir{rows.length === 1 ? "y" : "ies"}
          {rows.filter((r) => (r.status || "NEW") === "NEW").length > 0 &&
            ` · ${rows.filter((r) => (r.status || "NEW") === "NEW").length} new`}
        </div>
        <Button variant="outline" size="sm" className="h-8" onClick={load} data-testid="enq-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {busy && rows.length === 0 ? (
        <div className="text-center py-16 text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-md" data-testid="enq-empty">
          <Inbox className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="text-[14px] text-slate-600">No enquiries yet.</div>
          <p className="text-[12px] text-slate-400 mt-1">Share a collection publicly to start collecting leads.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((e) => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-md p-4"
              data-testid={`enq-row-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-slate-900">{e.name}</span>
                    <span className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${STATUS_TONE[e.status || "NEW"]}`}>
                      {e.status || "NEW"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[12px] text-slate-600">
                    <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-slate-900">
                      <Mail className="h-3.5 w-3.5" />{e.email}</a>
                    {e.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{e.phone}</span>}
                    {e.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{e.company}</span>}
                  </div>
                  <div className="mt-1.5 text-[11px] text-slate-400 inline-flex items-center gap-1">
                    <Layers className="h-3 w-3" /> {e.collection_name || "—"} · {fmtDate(e.created_at)}
                  </div>
                  {e.message && (
                    <div className="mt-2 text-[12px] text-slate-700 bg-slate-50 border border-slate-100 rounded px-3 py-2 inline-flex items-start gap-1.5 max-w-2xl">
                      <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                      <span>{e.message}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={e.status || "NEW"} onChange={(ev) => setStatus(e.id, ev.target.value)}
                    className="h-8 border border-slate-200 rounded px-2 text-[12px] bg-white"
                    data-testid={`enq-status-${e.id}`}>
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Button size="sm" className="h-8 bg-slate-900 text-white hover:bg-slate-800"
                    onClick={() => createQuote(e)} data-testid={`enq-quote-${e.id}`}>
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> Create quote
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.get("/catalogue-collections/analytics/summary")
      .then(({ data }) => setData(data))
      .catch(() => setData({ rows: [], totals: {} }))
      .finally(() => setBusy(false));
  }, []);

  if (busy && !data) return <div className="text-center py-16 text-slate-500">Loading…</div>;
  const t = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Collections shared", t.collections, Layers],
          ["Live + past links", t.links, BarChart3],
          ["Total views", t.views, Eye],
          ["Total enquiries", t.enquiries, Inbox]].map(([label, val, Icon]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`analytics-stat-${label}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
              <Icon className="h-4 w-4 text-slate-300" />
            </div>
            <div className="text-2xl font-heading font-semibold text-slate-900 mt-2">{val ?? 0}</div>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-14 border border-dashed rounded-md text-slate-500 text-[13px]" data-testid="analytics-empty">
          No shared links yet. Share a collection to see its performance here.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <th className="text-left font-medium px-4 py-2.5">Collection</th>
                <th className="text-right font-medium px-4 py-2.5">Links</th>
                <th className="text-right font-medium px-4 py-2.5">Active</th>
                <th className="text-right font-medium px-4 py-2.5">Views</th>
                <th className="text-right font-medium px-4 py-2.5">Enquiries</th>
                <th className="text-right font-medium px-4 py-2.5">Conv.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const conv = r.views ? Math.round((r.enquiries / r.views) * 100) : 0;
                return (
                  <tr key={r.collection_id} className="hover:bg-slate-50" data-testid={`analytics-row-${r.collection_id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.collection_name || "—"}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.links}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.active_links}</td>
                    <td className="px-4 py-3 text-right text-slate-900 font-medium">{r.views}</td>
                    <td className="px-4 py-3 text-right text-slate-900 font-medium">{r.enquiries}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{conv}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
